// src/talent/components/MeasurementsDialog.jsx

import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import api from "../../api/api";
import { ENDPOINTS } from "../../api/endpoints";

/* ------------------------------------------------------
   Helpers
------------------------------------------------------- */
const getEmptyForm = () => ({
  heightStart: "",
  heightEnd: "",
  weight: "",

  eyeColorName: "",
  hairColorName: "",

  // men
  maleSuit: "",
  maleShirt: "",
  maleWaist: "",
  maleInseam: "",
  maleShoe: "",

  // women
  dress: "",
  bust: "",
  waist: "",
  hips: "",
  shoe: "",
});

export default function MeasurementsDialog({
  open,
  onClose,
  talentData = {},
  onSave,
}) {
  const [form, setForm] = useState(getEmptyForm());
  const [eyeColors, setEyeColors] = useState([]);
  const [hairColors, setHairColors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // 🔒 Prevent dev-mode double submit
  const savingRef = useRef(false);

  /* ------------------------------------------------------
     Load initial values
  ------------------------------------------------------- */
  useEffect(() => {
    if (!open) return;

    fetchEyeColors();
    fetchHairColors();

    setForm({
      ...getEmptyForm(),

      heightStart: talentData.heightStart || "",
      heightEnd: talentData.heightEnd || "",
      weight: talentData.weight || "",

      eyeColorName: talentData.eyeColor || "",
      hairColorName: talentData.hairColor || "",

      maleSuit: talentData.maleSuit || "",
      maleShirt: talentData.maleShirt || "",
      maleWaist: talentData.maleWaist || "",
      maleInseam: talentData.maleInseam || "",
      maleShoe: talentData.maleShoe || "",

      dress: talentData.dress || "",
      bust: talentData.bust || "",
      waist: talentData.waist || "",
      hips: talentData.hips || "",
      shoe: talentData.shoe || "",
    });
  }, [open, talentData]);

  const fetchEyeColors = async () => {
    try {
      const resp = await api.get(ENDPOINTS.getEyeColors());
      setEyeColors((resp.data.data || []).filter((c) => c.activeFlag));
    } catch (err) {
      console.error("Error loading eye colors", err);
    }
  };

  const fetchHairColors = async () => {
    try {
      const resp = await api.get(ENDPOINTS.getHairColors());
      setHairColors((resp.data.data || []).filter((c) => c.activeFlag));
    } catch (err) {
      console.error("Error loading hair colors", err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleClearAll = () => {
    setForm(getEmptyForm());
    setError("");
  };

  /* ------------------------------------------------------
     SAVE — Height, Weight, Eye & Hair mandatory
  ------------------------------------------------------- */
  const handleSave = async () => {
    if (savingRef.current) return;
    savingRef.current = true;

    setLoading(true);
    setError("");

    if (
      !form.heightStart ||
      !form.heightEnd ||
      !form.weight ||
      !form.eyeColorName ||
      !form.hairColorName
    ) {
      setError("Height, weight, eye color and hair color are required.");
      setLoading(false);
      savingRef.current = false;
      return;
    }

    const eye = eyeColors.find(
      (c) => c.eyeColorName === form.eyeColorName
    );
    const hair = hairColors.find(
      (c) => c.hairColorName === form.hairColorName
    );

    if (!eye || !hair) {
      setError("Please select valid eye and hair colors.");
      setLoading(false);
      savingRef.current = false;
      return;
    }

    const payload = {
      userId: Number(
        talentData.userId ??
          localStorage.getItem("userId") ??
          0
      ),

      firstName: talentData.firstName || "",
      middleName: talentData.middleName || "",
      lastName: talentData.lastName || "",

      sex: talentData.sex || "",
      primaryContactNumber: talentData.primaryContactNumber || "",
      stateCode: talentData.stateCode || "",
      email: talentData.email || "",

      ageRangeFrom: Number(talentData.ageRangeFrom ?? 0),
      ageRangeTo: Number(talentData.ageRangeTo ?? 0),
      ethnicityCode: Number(talentData.ethnicityCode ?? 0),

      eyeColorCode: Number(eye.eyeColorId),
      hairColorCode: Number(hair.hairColorId),

      heightStart: Number(form.heightStart),
      heightEnd: Number(form.heightEnd),
      weight: Number(form.weight),

      vocalRangeID: Number(talentData.vocalRangeID ?? 0),

      // women
      dress: form.dress || "",
      bust: form.bust || "",
      waist: form.waist || "",
      hips: form.hips || "",
      shoe: form.shoe || "",

      // men
      maleSuit: form.maleSuit || "",
      maleShirt: form.maleShirt || "",
      maleWaist: form.maleWaist || "",
      maleInseam: form.maleInseam || "",
      maleShoe: form.maleShoe || "",

      hasPassport: Boolean(talentData.hasPassport),
      hasDriverLicencse: Boolean(talentData.hasDriverLicencse),
    };

    try {
      const resp = await api.post(
        ENDPOINTS.updateTalentProfile(),
        payload
      );

      if (
        resp.status === 200 &&
        (resp.data.success || resp.data.status === "Ok")
      ) {
        onSave({
          ...form,
          eyeColor: form.eyeColorName,
          hairColor: form.hairColorName,
          eyeColorCode: payload.eyeColorCode,
          hairColorCode: payload.hairColorCode,
        });
        onClose();
      } else {
        throw new Error(resp.data.message || "Save failed");
      }
    } catch (err) {
      setError(err.message || "Unexpected error");
    } finally {
      setLoading(false);
      savingRef.current = false;
    }
  };

  const isSaveDisabled =
    loading ||
    !form.heightStart ||
    !form.heightEnd ||
    !form.weight ||
    !form.eyeColorName ||
    !form.hairColorName;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit Measurements</DialogTitle>

      <DialogContent dividers>
        {error && <Typography color="error">{error}</Typography>}

        {/* CORE */}
        <Typography sx={{ mb: 1 }} variant="subtitle1">
          Core Measurements <span style={{ color: "red" }}>*</span>
        </Typography>

        <Grid container spacing={2} mb={2}>
          <Grid item xs={6}>
            <TextField
              label="Height (feet)"
              name="heightStart"
              value={form.heightStart}
              onChange={handleChange}
              fullWidth
              required
              error={!form.heightStart}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Height (inches)"
              name="heightEnd"
              value={form.heightEnd}
              onChange={handleChange}
              fullWidth
              required
              error={!form.heightEnd}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Weight (lbs)"
              name="weight"
              value={form.weight}
              onChange={handleChange}
              fullWidth
              required
              error={!form.weight}
            />
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth required error={!form.eyeColorName}>
              <InputLabel>Eye Color</InputLabel>
              <Select
                name="eyeColorName"
                value={form.eyeColorName}
                label="Eye Color"
                onChange={handleChange}
              >
                {eyeColors.map((c) => (
                  <MenuItem key={c.eyeColorId} value={c.eyeColorName}>
                    {c.eyeColorName}
                  </MenuItem>
                ))}
              </Select>
              {!form.eyeColorName && (
                <FormHelperText>Eye color is required</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth required error={!form.hairColorName}>
              <InputLabel>Hair Color</InputLabel>
              <Select
                name="hairColorName"
                value={form.hairColorName}
                label="Hair Color"
                onChange={handleChange}
              >
                {hairColors.map((c) => (
                  <MenuItem key={c.hairColorId} value={c.hairColorName}>
                    {c.hairColorName}
                  </MenuItem>
                ))}
              </Select>
              {!form.hairColorName && (
                <FormHelperText>Hair color is required</FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>

        {/* MEN */}
        <Typography variant="subtitle1">Men’s Sizes</Typography>
        <Grid container spacing={2} mb={2}>
          {[
            ["Suit (chest in)", "maleSuit"],
            ["Shirt size", "maleShirt"],
            ["Waist (in)", "maleWaist"],
            ["Inseam (in)", "maleInseam"],
            ["Shoe (men)", "maleShoe"],
          ].map(([label, key]) => (
            <Grid item xs={6} key={key}>
              <TextField
                label={label}
                name={key}
                value={form[key]}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
          ))}
        </Grid>

        {/* WOMEN */}
        <Typography variant="subtitle1">Women’s Sizes</Typography>
        <Grid container spacing={2}>
          {[
            ["Dress (US)", "dress"],
            ["Bust (in)", "bust"],
            ["Waist (in)", "waist"],
            ["Hips (in)", "hips"],
            ["Shoe (women)", "shoe"],
          ].map(([label, key]) => (
            <Grid item xs={6} key={key}>
              <TextField
                label={label}
                name={key}
                value={form[key]}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
          ))}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Button onClick={handleClearAll} disabled={loading} variant="outlined">
          Clear All
        </Button>

        <div>
          <Button onClick={onClose} disabled={loading} variant="outlined">
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaveDisabled}
            sx={{ ml: 1 }}
          >
            {loading ? "Saving…" : "Save"}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
}
